<template>
  <v-card flat>
    <v-card-title class="py-0 px-0">
      <v-toolbar
        :height="$vuetify.breakpoint.xsOnly ? 44 : 60"
        class="filter-toolbar"
        flat
      >
        <v-btn
          v-if="posUserHasComponentPermission('Orders', 'ADD') && posUserHasComponentPermission('Pos', 'ACCESS')"
          :icon="$vuetify.breakpoint.xsOnly"
          :to="{name: 'Pos', exact: true}"
          class="primary--text elevation-0 ma-1"
          text
        >
          <v-icon :left="$vuetify.breakpoint.smAndUp">
            {{ $t('Order.OrdersAll.Toolbar.Button.Icon') }}
          </v-icon>&nbsp;
          <span v-if="$vuetify.breakpoint.smAndUp">{{
            $t('Order.OrdersAll.Toolbar.Button.Title').toLocaleUpperCase($i18n.locale)
          }}</span>
        </v-btn>

        <reports-button
          v-if="posUserHasComponentPermission('Orders', 'MANAGE') && posUser?.Settings?.AllUsers?.Permissions?.CanPrintReportZ && (userCanViewAllOrders || userCanViewFiscalDoc)"
          :disable-tooltip="!$vuetify.breakpoint.xsOnly"
          :fab="$vuetify.breakpoint.xsOnly"
          :hide-label="$vuetify.breakpoint.xsOnly"
          :orders-report-history="posUserHasComponentPermission('ReportsNotify', 'ACCESS')"
          :receipts-report="userCanViewFiscalDoc"
          :receipts-report-history="posUserHasComponentPermission('ReportsNotify', 'ACCESS')"
          depressed
          flat
          orders-report
          orders-report-email
          orders-report-pdf
          orders-report-print
          receipts-report-email
          receipts-report-pdf
          receipts-report-print
          @print-orders-report="printer => showSelectDateRangeDialog(printer, 'orderTotalsZ', 'print')"
          @print-receipts-report="printer => showSelectDateRangeDialog(printer, 'receiptTotalsZ', 'print')"
          @download-pdf-orders-report="() => showSelectDateRangeDialog(null, 'orderTotalsZ', 'pdf',)"
          @download-pdf-receipts-report="() => showSelectDateRangeDialog(null, 'receiptTotalsZ', 'pdf')"
          @send-email-orders-report="() => showSelectDateRangeDialog(null, 'orderTotalsZ', 'email')"
          @send-email-receipts-report="() => showSelectDateRangeDialog(null, 'receiptTotalsZ', 'email')"
        />

        <v-spacer />

        <table-actions
          v-if="userCanDeleteOrders"
          v-model="dataTable.tableActions"
          :disabled="!viewHasData"
          :items.sync="selectedOrders"
        >
          <v-btn
            v-if="userCanDeleteOrders"
            :color="$vuetify.theme.dark ? '#1E1E1E' : 'rgba(0, 0, 0, 0.06)'"
            :disabled="deleteOrderLoading || !selectedOrders.length"
            :loading="deleteOrderLoading"
            :style="$vuetify.breakpoint.xsOnly ? 'width: 36px; min-width: 36px; height: 36px;' : 'width: 48px; min-width: 48px; height: 48px;'"
            class="pa-0 ma-1 elevation-0"
            @click="onDeleteOrdersClick(selectedOrders)"
          >
            <v-icon
              color="red"
              v-text="'delete'"
            />
          </v-btn>
        </table-actions>

        <filter-search v-model="dataTable.search.terms" />

        <filter-options
          v-model="filtersModel"
          :filters="filterData"
          max-width="500"
        />
      </v-toolbar>
    </v-card-title>

    <!--ALL ORDERS TABLE-->
    <v-data-table
      v-model="selectedOrders"
      :footer-props="dataTable.footerProps"
      :headers="dataTableHeaders"
      :height="dataTable.resize.bodyHeight"
      :items="filteredItems"
      :loading="dataTable.loading"
      :loading-text="dataTable.text.loadingText"
      :no-data-text="dataTable.text.noDataText"
      :no-results-text="dataTable.text.noResultsText"
      :options.sync="dataTable.options"
      :search="dataTable.search.terms"
      :server-items-length="dataTable.total"
      :show-select="dataTable.tableActions"
      :single-select="false"
      fixed-header
      item-key="Id"
    >
      <template #[`item.source_type`]="{item}">
        <div v-if="getOrderSourceTypeById(item.SourceTypeId)">
          <v-tooltip
            color="indigo lighten-2"
            right
          >
            <template #activator="{on}">
              <div v-on="on">
                <v-badge
                  v-model="item.IsTest"
                  bordered
                  color="deep-orange"
                  overlap
                >
                  <template #badge>
                    <span class="font-weight-bold">TEST</span>
                  </template>

                  <v-avatar size="28">
                    <v-icon
                      v-if="item.SourceTypeId < 100"
                      color="indigo lighten-2"
                    >
                      {{ getOrderSourceTypeById(item.SourceTypeId) && getOrderSourceTypeById(item.SourceTypeId).logo }}
                    </v-icon>

                    <img
                      v-else
                      :src="getOrderSourceTypeById(item.SourceTypeId) && getOrderSourceTypeById(item.SourceTypeId).logo"
                    >
                  </v-avatar>
                </v-badge>
              </div>
            </template>

            <div v-if="item.IsTest">
              {{ $t('Common.Misc.TestOrder') }}
            </div>
            <span v-if="item.SourceTypeId < 100">
              {{ getOrderSourceTypeLangById(item.SourceTypeId) && getOrderSourceTypeLangById(item.SourceTypeId).Desc }}
            </span>
            <span v-else>
              {{ getOrderSourceTypeById(item.SourceTypeId).desc }}
            </span>
          </v-tooltip>
        </div>
      </template>

      <template #[`item.daily_check`]="{item}">
        <v-chip
          v-if="item.DailyCheck && appConfig.LOCATION_DATA.ShowDailyCheck"
          class="px-1"
          label
          x-small
        >
          {{ item.DailyCheck }}
        </v-chip>
      </template>

      <template #[`item.order_id`]="{item}">
        <div class="d-flex align-center">
          <template v-if="item.ExternalPlatform && item.ExternalCatalogId && getExternalOrderRawUrl(item) && userIsSuperAdmin">
            <div class="d-inline-flex mr-2">
              <v-menu
                bottom
                offset-y
                transition="slide-y-transition"
              >
                <template #activator="{on: onMenu}">
                  <v-tooltip
                    color="indigo"
                    top
                  >
                    <template #activator="{on: onTooltip}">
                      <v-btn
                        class="indigo"
                        dark
                        icon
                        small
                        target="_blank"
                        v-on="{...onMenu, ...onTooltip}"
                      >
                        <v-icon small>
                          mdi-refresh
                        </v-icon>
                      </v-btn>
                    </template>

                    <span>Retry External Order</span>
                  </v-tooltip>
                </template>

                <v-card>
                  <v-card-text class="pa-2">
                    <v-btn
                      block
                      class="green my-1"
                      dark
                      text
                      @click="retryExternalOrder(item, false)"
                    >
                      Retry No Broadcast
                    </v-btn>

                    <v-btn
                      block
                      class="primary my-1"
                      text
                      @click="retryExternalOrder(item, true)"
                    >
                      Retry WITH Broadcast
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>

            <div class="d-inline-flex mr-2">
              <v-tooltip
                color="light-green"
                top
              >
                <template #activator="{on}">
                  <v-btn
                    :href="getExternalOrderRawUrl(item)"
                    class="light-green"
                    dark
                    icon
                    small
                    target="_blank"
                    v-on="on"
                  >
                    <v-icon small>
                      mdi-code-block-braces
                    </v-icon>
                  </v-btn>
                </template>

                <span>View External Order</span>
              </v-tooltip>
            </div>
          </template>

          <div class="d-inline-flex">
            <div>
              <a
                href="#"
                @click.prevent="getOrderDetails(item)"
              >{{ item.OrderCode }}</a>

              <template v-if="item.IsEdited">
                <v-tooltip
                  color="purple"
                  top
                >
                  <template #activator="{on}">
                    <v-avatar
                      class="ml-1"
                      color="purple"
                      size="16"
                      v-on="on"
                    >
                      <v-icon
                        dark
                        x-small
                        v-text="$t('Order.OrdersAll.ListView.Button.Edit.Icon')"
                      />
                    </v-avatar>
                  </template>

                  <span>{{ $t('Order.OrdersAll.ListView.Button.Edit.Edited') }}</span>
                </v-tooltip>
              </template>

              <div
                v-if="item.ExternalPlatform"
                class="text-caption text-uppercase"
              >
                #{{ item.ExternalOrderId }}
              </div>

              <div
                v-if="item.MarketplaceOrderCode"
                class="text-caption text-uppercase"
              >
                #{{ item.MarketplaceOrderCode }}
              </div>

              <div
                v-if="item.QrData"
                class="text-caption"
              >
                {{ item.QrData }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #[`item.icons`]="{item}">
        <v-tooltip
          :color="parseInt(item.CustomerId) === 0 ? 'grey darken-3' : 'amber darken-3'"
          right
        >
          <template #activator="{on}">
            <v-icon
              :color="parseInt(item.CustomerId) === 0 ? 'grey' : 'amber'"
              class="text--lighten-2"
              small
              v-on="on"
            >
              verified_user
            </v-icon>
          </template>
          <span>{{ parseInt(item.CustomerId) === 0 ? $t('Customer.CustomerType.Visitor') : $t('Customer.CustomerType.Registered') }}</span>
        </v-tooltip>

        <div v-if="parseInt(item.AddressId) > -1 && item.Address && parseInt(item.OrderTypeId) === 1">
          <v-icon
            color="light-green lighten-1"
            small
          >
            location_on
          </v-icon>
        </div>

        <div v-if="item.Telephone && $options.filters.phone(item.Telephone)">
          <v-icon
            color="blue lighten-2"
            small
          >
            phone
          </v-icon>
        </div>

        <div v-if="item.Telephone2 && $options.filters.phone(item.Telephone2)">
          <v-icon
            color="blue lighten-2"
            small
          >
            phone
          </v-icon>
        </div>

        <div v-if="item.Telephone3 && $options.filters.phone(item.Telephone3)">
          <v-icon
            color="blue lighten-2"
            small
          >
            phone
          </v-icon>
        </div>
      </template>

      <template #[`item.customer`]="{item}">
        <div
          v-if="posUserHasComponentPermission('CustomerDetails', 'ACCESS') && parseInt(item.CustomerId) !== 0"
          :class="isDarkMode ? 'white--text' : 'grey--text text--darken-3'"
          :style="customerColumnMaxWidth"
          style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; cursor: pointer;"
          @click="$bus.$emit('show-customer-details-dialog', item.CustomerId)"
        >
          {{ item.FullName }}
        </div>

        <template v-else>
          <div
            :class="isDarkMode ? 'white--text' : 'grey--text text--darken-3'"
            :style="customerColumnMaxWidth"
            style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
          >
            {{ parseInt(item.CustomerId) !== 0 || item.FullName.trim() ? item.FullName : $t('Customer.CustomerType.Visitor') }}
          </div>
        </template>

        <div
          v-if="!isQrOrder(item) && parseInt(item.AddressId) > -1 && item.Address && parseInt(item.OrderTypeId) === 1"
          :style="customerColumnMaxWidth"
          class="grey--text"
          style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
        >
          <address-display :address="item?.Address" />
        </div>

        <div
          v-else-if="isQrOrder(item) && parseInt(item.OrderTypeId) === 1"
          :style="customerColumnMaxWidth"
          class="grey--text"
          style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
        >
          {{ item.Comment }}
        </div>

        <div
          v-if="item.Telephone || item.Telephone2 || item.Telephone3"
          :style="customerColumnMaxWidth"
          class="grey--text"
          style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
        >
          <template v-if="item.Telephone && $options.filters.phone(item.Telephone)">
            {{ item.Telephone | phone }}<br>
          </template>

          <template v-if="item.Telephone2 && $options.filters.phone(item.Telephone2)">
            {{ item.Telephone2 | phone }}<br>
          </template>

          <template v-if="item.Telephone3 && $options.filters.phone(item.Telephone3)">
            {{ item.Telephone3 | phone }}<br>
          </template>
        </div>
      </template>

      <template #[`item.staff_id`]="{item}">
        <user-chip
          v-if="item.StaffId > 0"
          :color="item.StaffColor"
          :full-name="item.StaffFirstname + ' ' + item.StaffLastname"
          :short-name="item.StaffShortname"
        />
      </template>

      <template #[`item.status`]="{item}">
        <div class="my-1">
          <order-status-chip
            :order="item"
            dense
            min-width="186"
          />
        </div>

        <div class="my-1">
          <order-status-actions
            v-if="!item.IsScheduled || (item.IsScheduled && (item.ScheduledToday || item.ScheduledExpired))"
            :can-update-order-time="canUpdateOrderTime(item)"
            :order="item"
          />
        </div>
      </template>

      <template #[`item.order_type`]="{item}">
        <div class="my-1">
          <div
            v-if="parseInt(item.SourceTypeId) === SourceTypeEnum.TABLE && item.Table"
            class="font-weight-medium"
            style="font-size: 11px"
          >
            {{ item.Table?.TableAreaName }} #{{ item.Table?.TableName }}
          </div>

          <order-type-chip
            :order="item"
            dense
          />
        </div>

        <div class="my-1">
          <order-delivery-status-chip
            :order="item"
            dense
          />
        </div>
      </template>

      <template #[`item.order_total`]="{item}">
        <v-badge
          :class="{'pulse-badge': orderIsGeneratingFiscalDocument(item)}"
          :color="orderFiscalDocumentBadgeColor(item)"
          :value="appHasFiscalDocsEnabled && appHasFiscalDocsProvider && (orderHasFiscalDocument(item, true) || (orderCanGenerateFiscalDocument(item) && orderIsRefundable(item)) || orderIsGeneratingFiscalDocument(item) )"
          class="receipts-badge"
          offset-x="8"
          offset-y="2"
          right
        >
          <v-tooltip
            v-if="(appHasFiscalDocsEnabled && appHasFiscalDocsProvider) || orderHasFiscalDocument(item, true)"
            :color="orderFiscalDocumentBadgeColor(item)"
            content-class="text-center"
            max-width="350"
            right
          >
            <template #activator="{on}">
              <div
                class="d-inline-block"
                v-on="on"
              >
                <template v-if="orderHasUnpaidBalance(item) && parseInt(item.StatusId) !== OrderStatusEnum.Canceled">
                  <span class="red--text">{{ orderTransactionsTotal(item) | currency }}</span> <span>/</span> <span class="blue--text">{{ item.Price | currency }}</span>
                </template>
                <template v-else>
                  {{ item.Price | currency }}
                </template>
              </div>
            </template>

            <div v-if="orderHasUnpaidBalance(item) && parseInt(item.StatusId) !== OrderStatusEnum.Canceled">
              {{ $t('Order.Total.PartiallyPaid') }}
            </div>
            <div v-else-if="orderHasReceipt(item, orderLatestFiscalDocumentHasError(item)) && orderHasInvoice(item, orderLatestFiscalDocumentHasError(item))">
              <div>
                {{ $t('Order.Total.Receipt') }}

                <v-chip
                  v-if="orderReceiptsCount(item, orderLatestFiscalDocumentHasError(item)) > 0"
                  class="px-2 ml-2 mt-1 float-end"
                  x-small
                >
                  {{ orderReceiptsCount(item, orderLatestFiscalDocumentHasError(item)) }}
                </v-chip>
              </div>

              <div>
                {{ $t('Order.Total.Invoice') }}

                <v-chip
                  v-if="orderInvoicesCount(item, orderLatestFiscalDocumentHasError(item)) > 0"
                  class="px-2 ml-2 mt-1 float-end"
                  x-small
                >
                  {{ orderInvoicesCount(item, orderLatestFiscalDocumentHasError(item)) }}
                </v-chip>
              </div>
            </div>
            <div v-else-if="orderHasReceipt(item, orderLatestFiscalDocumentHasError(item))">
              {{ $t('Order.Total.Receipt') }}

              <v-chip
                v-if="orderReceiptsCount(item, orderLatestFiscalDocumentHasError(item)) > 0"
                class="px-2 ml-2 mt-1 float-end"
                x-small
              >
                {{ orderReceiptsCount(item, orderLatestFiscalDocumentHasError(item)) }}
              </v-chip>
            </div>
            <div v-else-if="orderHasInvoice(item, orderLatestFiscalDocumentHasError(item))">
              {{ $t('Order.Total.Invoice') }}

              <v-chip
                v-if="orderInvoicesCount(item, orderLatestFiscalDocumentHasError(item)) > 0"
                class="px-2 ml-2 mt-1 float-end"
                x-small
              >
                {{ orderInvoicesCount(item, orderLatestFiscalDocumentHasError(item)) }}
              </v-chip>
            </div>
            <div v-else-if="orderIsGeneratingFiscalDocument(item)">
              {{ $t('Order.Total.GeneratingDoc') }}
            </div>
            <div v-else>
              {{ $t('Order.Total.NoFiscalDoc') }}
            </div>

            <div
              v-if="orderLatestFiscalDocumentHasError(item)"
              class="mt-2"
            >
              <template v-for="receipt in orderFiscalDocuments(item, true)">
                <div
                  :key="`receipt-${receipt.ReceiptId}`"
                  class="mb-1"
                >
                  <template v-for="(error, index) in orderFiscalDocumentErrors(receipt)">
                    <div
                      :key="`receipt-${receipt.ReceiptId}-error-${index}`"
                      class="text-left mb-1"
                    >
                      <span class="font-weight-bold">{{ error.code }}</span> - {{ error.message }}
                    </div>
                  </template>
                </div>
              </template>
            </div>
          </v-tooltip>

          <div
            v-else
            class="d-inline-block"
          >
            <template v-if="orderHasUnpaidBalance(item) && parseInt(item.StatusId) !== OrderStatusEnum.Canceled">
              <span class="red--text">{{ orderTransactionsTotal(item) | currency }}</span> <span>/</span> <span class="blue--text">{{ item.Price | currency }}</span>
            </template>
            <template v-else>
              {{ item.Price | currency }}
            </template>
          </div>

          <template #badge>
            {{ item?.ReceiptsCount || '' }}
          </template>
        </v-badge>
      </template>

      <template #[`item.order_payment`]="{item}">
        <order-payments-icon
          :item="item"
          chip
          class="ml-1"
          dense
        />
      </template>

      <template #[`item.date_added`]="{item}">
        <template v-if="item.IsScheduled">
          <span>{{ item.ScheduledDateAt }}</span>

          <div class="grey--text">
            {{ item.ScheduledTimeAt }}
          </div>
        </template>

        <template v-else>
          <span>{{ item.DateAdded }}</span>

          <div class="grey--text">
            {{ item.TimeAdded }}
          </div>
        </template>
      </template>

      <template
        v-if="appHasWebhooksOrderActive && (userIsOwner || userIsSuperAdmin)"
        #[`item.webhook_successful`]="{item}"
      >
        <status-chip
          v-if="item?.Webhook?.Successful !== null"
          :avatar-color="getOrderWebhookStatus(item).Color"
          :chip-style="userIsSuperAdmin ? {cursor: 'pointer'} : ''"
          :color="getOrderWebhookStatus(item).Color"
          :icon="getOrderWebhookStatus(item).Icon"
          :icon-only="true"
          :label="getOrderWebhookStatus(item).Name"
          :tooltip="getOrderWebhookStatus(item).Tooltip"
          dense
          @click="getOrderWebhookHistory(item)"
        />
      </template>

      <template #[`item.actions`]="{item}">
        <v-btn
          v-if="dataTable.tableActions"
          :small="$vuetify.breakpoint.xsOnly"
          class="blue-grey lighten-2 elevation-2 ma-0"
          dark
          disabled
          icon
        >
          <v-icon :small="$vuetify.breakpoint.xsOnly">
            menu
          </v-icon>
        </v-btn>

        <v-speed-dial
          v-else
          v-model="item.fab"
          direction="left"
          transition="slide-x-reverse-transition"
        >
          <template #activator>
            <v-btn
              :small="$vuetify.breakpoint.xsOnly"
              class="blue-grey lighten-2 elevation-2 ma-0"
              dark
              icon
            >
              <v-icon
                v-if="item.fab"
                :small="$vuetify.breakpoint.xsOnly"
              >
                close
              </v-icon>
              <v-icon
                v-else
                :small="$vuetify.breakpoint.xsOnly"
              >
                menu
              </v-icon>
            </v-btn>
          </template>

          <issue-receipt-document-button
            v-if="issueReceiptDocumentVisible(item) && (orderCanGenerateFiscalDocument(item) || (!orderCanGenerateFiscalDocument(item) && orderHasFiscalDocument(item)))"
            :badge-offset-x="20"
            :badge-offset-y="20"
            :can-cancel-document="posUserCan('RECEIPTS', 'MANAGE')"
            :can-convert-receipt="isOrderToday(item) && appCanConvertReceiptToInvoice"
            :can-issue-document="orderCanGenerateFiscalDocument(item)"
            :document-id="orderLatestFiscalDocument(item)?.ReceiptId"
            :document-type="orderLatestFiscalDocument(item)?.ReceiptType"
            :document-url="orderLatestFiscalDocument(item)?.DocumentUrl"
            :from="EmailNotifyFromEnum.ORDER"
            :icon-name="orderHasFiscalDocument(item) ? 'mdi-receipt-text-edit-outline' : 'mdi-receipt-text-plus-outline'"
            :invoice="appHasInvoiceDocEnabled && appFiscalDocsProviderDocument.includes(FiscalDocTypeEnum.INVOICE)"
            :label="orderHasFiscalDocument(item) ? $t('Common.Button.ManageDocument').toLocaleUpperCase($i18n.locale) : $t('Common.Button.IssueDocument').toLocaleUpperCase($i18n.locale)"
            :receipt="appHasReceiptDocEnabled && appFiscalDocsProviderDocument.includes(FiscalDocTypeEnum.RECEIPT)"
            :small="$vuetify.breakpoint.xsOnly"
            :tooltip="orderHasFiscalDocument(item) ? orderHasReceipt(item) ? $t('Common.Button.ManageReceipt') : orderHasInvoice(item) ? $t('Common.Button.ManageInvoice') : $t('Common.Button.ManageFnb') : $t('Common.Button.IssueDocument')"
            :tooltip-open-delay="150"
            can-cancel-fnb
            hide-label
            icon
            tooltip-position="top"
            @view-document="data => $bus.$emit('fiscal-document:view', data.DocUrl)"
            @issue-document="data => $bus.$emit('fiscal-document:create', item, data.DocType, (data.Print === false || data.Print === true) ? {Print: data.Print} : null)"
            @cancel-document="data => $bus.$emit('fiscal-document:cancel', orderLatestFiscalDocument(item), data)"
            @print-document="data => $bus.$emit('fiscal-document:print', data.DocId)"
            @email-document="data => $bus.$emit('fiscal-document:email', data.DocId, item, data)"
            @convert-receipt="data => $bus.$emit('fiscal-document:convert-to-invoice', orderLatestFiscalDocument(item), data)"
          />

          <print-button
            v-if="printOrderDocumentVisible(item)"
            :small="$vuetify.breakpoint.xsOnly"
            :tooltip-open-delay="150"
            hide-label
            icon
            menu-offset-y
            open-on-hover
            tooltip-position="top"
            @printer-selected="printer => printOrder(item, printer)"
          />

          <template v-if="item.SourceTypeId !== SourceTypeEnum.TABLE && !SourceTypeEnum.IS_MARKETPLACE_SOURCE(item.SourceTypeId) && canEditOrder(item)">
            <v-tooltip
              :color="$t('Order.OrdersAll.ListView.Button.Edit.Color')"
              top
            >
              <template #activator="{on}">
                <v-btn
                  :class="$t('Order.OrdersAll.ListView.Button.Edit.Color')"
                  :small="$vuetify.breakpoint.xsOnly"
                  dark
                  icon
                  @click="showOrderEditDialog(item)"
                  v-on="on"
                >
                  <v-icon :small="$vuetify.breakpoint.xsOnly">
                    {{ $t('Order.OrdersAll.ListView.Button.Edit.Icon') }}
                  </v-icon>
                </v-btn>
              </template>

              <span>{{ $t('Order.OrdersAll.ListView.Button.Edit.Title') }}</span>
            </v-tooltip>
          </template>

          <v-tooltip
            :color="$t('Order.OrdersAll.ListView.Button.View.Color')"
            top
          >
            <template #activator="{on}">
              <v-btn
                :class="$t('Order.OrdersAll.ListView.Button.View.Color')"
                :small="$vuetify.breakpoint.xsOnly"
                dark
                icon
                @click="getOrderDetails(item)"
                v-on="on"
              >
                <v-icon :small="$vuetify.breakpoint.xsOnly">
                  {{ $t('Order.OrdersAll.ListView.Button.View.Icon') }}
                </v-icon>
              </v-btn>
            </template>

            <span>{{ $t('Order.OrdersAll.ListView.Button.View.Title') }}</span>
          </v-tooltip>

          <template v-if="parseInt(item.StatusId) !== OrderStatusEnum.Canceled && posUserHasComponentPermission('Orders', 'DELETE')">
            <v-tooltip
              :color="$t('Order.OrdersAll.ListView.Button.Cancel.Color')"
              top
            >
              <template #activator="{on}">
                <v-btn
                  :class="$t('Order.OrdersAll.ListView.Button.Cancel.Color')"
                  :small="$vuetify.breakpoint.xsOnly"
                  dark
                  icon
                  @click="onCancelOrderClick(item)"
                  v-on="on"
                >
                  <v-icon :small="$vuetify.breakpoint.xsOnly">
                    {{ $t('Order.OrdersAll.ListView.Button.Cancel.Icon') }}
                  </v-icon>
                </v-btn>
              </template>

              <span>{{ $t('Order.OrdersAll.ListView.Button.Cancel.Title') }}</span>
            </v-tooltip>
          </template>

          <template v-if="parseInt(item.StatusId) === OrderStatusEnum.Canceled && posUserHasComponentPermission('Orders', 'MANAGE') && (userIsOwner || userIsSuperAdmin)">
            <v-tooltip
              :color="$t('Order.OrdersAll.ListView.Button.Revert.Color')"
              top
            >
              <template #activator="{on}">
                <v-btn
                  :class="$t('Order.OrdersAll.ListView.Button.Revert.Color')"
                  :small="$vuetify.breakpoint.xsOnly"
                  dark
                  icon
                  @click="onRevertOrderClick(item)"
                  v-on="on"
                >
                  <v-icon :small="$vuetify.breakpoint.xsOnly">
                    {{ $t('Order.OrdersAll.ListView.Button.Revert.Icon') }}
                  </v-icon>
                </v-btn>
              </template>

              <span>{{ $t('Order.OrdersAll.ListView.Button.Revert.Title') }}</span>
            </v-tooltip>
          </template>

          <template
            v-if="!SourceTypeEnum.IS_MARKETPLACE_SOURCE(item.SourceTypeId) && (parseInt(item.StatusId) === OrderStatusEnum.Completed || parseInt(item.StatusId) === OrderStatusEnum.Canceled) && userCanDeleteOrder"
          >
            <v-tooltip
              :color="$t('Order.OrdersAll.ListView.Button.Delete.Color')"
              top
            >
              <template #activator="{on}">
                <v-btn
                  :class="$t('Order.OrdersAll.ListView.Button.Delete.Color')"
                  :small="$vuetify.breakpoint.xsOnly"
                  dark
                  icon
                  @click="onDeleteOrderClick(item)"
                  v-on="on"
                >
                  <v-icon :small="$vuetify.breakpoint.xsOnly">
                    {{ $t('Order.OrdersAll.ListView.Button.Delete.Icon') }}
                  </v-icon>
                </v-btn>
              </template>

              <span>{{ $t('Order.OrdersAll.ListView.Button.Delete.Title') }}</span>
            </v-tooltip>
          </template>
        </v-speed-dial>
      </template>
    </v-data-table>

    <order-change-status-dialog
      :cancel-order-confirm-dialog-visible.sync="cancelOrderConfirmDialogVisible"
      :selected-order="!Array.isArray(selectedOrder) ? selectedOrder : undefined"
    />

    <confirm-dialog
      :html-content="$t('Order.OrderChangeStatusDialog.RevertStatus.Subtitle', {orderCode: selectedOrder?.OrderCode || ''})"
      :html-title="$t('Order.OrderChangeStatusDialog.RevertStatus.Title')"
      :visible.sync="revertOrderStatusConfirmDialogVisible"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      @confirm-dialog-yes="revertOrderStatus"
    />

    <order-delete-dialog
      v-if="appConfig.LOCATION_DATA.HasForceDeleteOrders && posUserHasComponentPermission('Orders', 'DELETE')"
      :is-delete-and-copy-order="isDeleteAndCopyOrder"
      :selected-order="selectedOrder"
      :visible.sync="deleteOrderConfirmDialogVisible"
      @order-deleted="onOrdersDelete"
    />

    <confirm-order-edit-dialog
      :selected-order="selectedOrder"
      :visible.sync="confirmDialogEditOrderVisible"
      @order-edit="onEditAdvanced"
      @order-edit-copy="onEditCopy"
      @order-edit-delete="onEditDelete"
    />

    <export-totals-z-dialog
      :action="selectedPrinter?.action || 'print'"
      :select-guid="selectedPrinter?.type === 'receiptTotalsZ'"
      :select-range="true"
      :select-staff="selectedPrinter?.type === 'receiptTotalsZ'"
      :select-type="selectedPrinter?.type === 'receiptTotalsZ'"
      :type="selectedPrinter?.type || 'receiptTotalsZ'"
      :visible.sync="selectDateRangeDialogVisible"
      @date-range="onPrintReceiptTotalsZDateRangeSelected"
    />

    <order-webhook-history-dialog
      v-if="userIsSuperAdmin && webhookHistoryDialogVisible"
      :data="selectedOrderWebhookHistory"
      :visible.sync="webhookHistoryDialogVisible"
      @retry="orderWebhookRetry"
    />

    <email-select-dialog
      :extra-data="emailSelectDialogExtraData"
      :visible.sync="emailSelectDialogVisible"
      use-user-email
      @save:dialog="onEmailSelected"
    />
  </v-card>
</template>

<script>
import OrdersCommon from '../../mixins/orders/common'
import Watchable from '../../mixins/watchable'
import PrintButton from '@/components/common/print/PrintButton.vue'
import FilterSearch from '@/components/common/filter/FilterSearch.vue'
import FilterOptions from '@/components/common/filter/FilterOptions.vue'
import FilterCommon from '@/mixins/filter/filterCommon'
import DataTable from '@/mixins/data-table/dataTable'
import OrderStatusEnum from '@/api/Enums/OrderStatusEnum'
import TableActions from '@/components/common/filter/TableActions.vue'
import IssueReceiptDocumentButton from '@/components/common/receipt/IssueReceiptDocumentButton.vue'
import FiscalDocTypeEnum from '@/api/Enums/FiscalDocTypeEnum'
import OrderPaymentsIcon from '@/components/orders/common/OrderPaymentsIcon.vue'
import PaymentTypeEnum from '@/api/Enums/PaymentTypeEnum'
import SourceTypeEnum from '@/api/Enums/SourceTypeEnum'
import {getContrastColor} from '@/lib/color'
import OrderStatusChip from '@/components/orders/common/OrderStatusChip.vue'
import OrderTypeChip from '@/components/orders/common/OrderTypeChip.vue'
import OrderDeliveryStatusChip from '@/components/orders/common/OrderDeliveryStatusChip.vue'
import ExportTotalsZDialog from '@/components/common/ExportTotalsZDialog.vue'
import StatusChip from '@/components/common/StatusChip.vue'
import OrderWebhookHistoryDialog from '@/components/orders/OrderWebhookHistoryDialog.vue'
import EmailNotifyFromEnum from '@/api/Enums/EmailNotifyFromEnum.js'
import FiscalDocsProviderTypeEnum from '@/api/Enums/FiscalDocsProviderTypeEnum.js'
import OrderStatusActions from '@/components/orders/common/OrderStatusActions.vue'
import AddressDisplay from '@/components/common/AddressDisplay.vue'
import {clone} from '@/lib/helper/helper.js'
import ConfirmOrderEditDialog from '@/components/orders/ConfirmOrderEditDialog.vue'
import ReportsButton from '@/components/common/print/ReportsButton.vue'
import EmailSelectDialog from '@/components/common/EmailSelectDialog.vue'
import SocketCommand from '@/api/SocketCommand.js'
import UserChip from '@/components/common/UserChip.vue'

export default {
  components: {
    UserChip,
    EmailSelectDialog,
    ReportsButton,
    ConfirmOrderEditDialog,
    AddressDisplay,
    OrderStatusActions,
    OrderWebhookHistoryDialog,
    StatusChip,
    ExportTotalsZDialog,
    OrderDeliveryStatusChip,
    IssueReceiptDocumentButton,
    OrderTypeChip,
    OrderStatusChip,
    OrderPaymentsIcon,
    TableActions,
    FilterOptions,
    FilterSearch,
    PrintButton
  },
  directives: {},
  mixins    : [OrdersCommon, Watchable, FilterCommon, DataTable],
  data() {
    return {
      dataTable: {
        dataStore    : 'ordersDataAll',
        socketCommand: {
          list: window.SocketCommand.Order.AllList
        },
        resize: {
          enabled        : true,
          debounceTimeout: null,
          offset         : -12,
          bodyHeight     : undefined
        },
        options: {
          defaultSortBy: 'order_id',
          sortBy       : ['order_id'],
          sortDesc     : [true]
        },
        rejectHeaders: {
          xsOnly   : ['icons'],
          smAndDown: [],
          mdAndDown: [],
          lgAndDown: [],
          xlAndDown: []
        },
        headers: [
          {
            text     : this.$t('Order.Headers.SourceType'),
            value    : 'source_type',
            align    : 'left',
            sortable : false,
            class    : 'px-1',
            cellClass: 'text-caption px-1'
          },
          {
            text     : '',
            value    : 'daily_check',
            align    : 'center',
            sortable : false,
            class    : 'px-1',
            cellClass: 'text-caption px-1',
            width    : 0
          },
          {
            text     : this.$t('Order.Headers.OrderId'),
            value    : 'order_id',
            align    : 'left',
            sortable : true,
            class    : 'px-1',
            cellClass: 'text-caption px-1'
          },
          {
            text     : '',
            value    : 'icons',
            align    : 'center',
            sortable : false,
            class    : 'px-1',
            cellClass: 'text-caption px-1'
          },
          {
            text     : this.$t('Order.Headers.Customer'),
            value    : 'customer',
            align    : 'left',
            sortable : false,
            class    : 'px-1',
            cellClass: 'text-caption px-1'
          },
          {
            text     : this.$t('Order.Headers.User'),
            value    : 'staff_id',
            align    : 'center',
            sortable : false,
            class    : 'px-1',
            cellClass: 'text-caption px-1'
          },
          {
            text     : this.$t('Order.Headers.Status'),
            value    : 'status',
            align    : 'center',
            sortable : false,
            class    : 'px-1',
            cellClass: 'text-caption px-1'
          },
          {
            text     : this.$t('Order.Headers.OrderType'),
            value    : 'order_type',
            align    : 'center',
            sortable : false,
            class    : 'px-1',
            cellClass: 'text-caption px-1'
          },
          {
            text     : this.$t('Order.Headers.Total'),
            value    : 'order_total',
            align    : 'right',
            sortable : true,
            class    : 'px-1',
            cellClass: 'text-caption px-1'
          },
          {
            text     : '',
            value    : 'order_payment',
            align    : 'center',
            sortable : false,
            class    : 'px-1',
            cellClass: 'text-caption px-1',
            width    : 25
          },
          {
            text     : this.$t('Order.Headers.Date'),
            value    : 'date_added',
            align    : 'center',
            sortable : true,
            class    : 'px-1',
            cellClass: 'text-caption px-1'
          },
          {
            text     : '',
            value    : 'actions',
            align    : 'right',
            sortable : false,
            class    : 'px-1',
            cellClass: 'text-caption px-1',
            width    : 40
          }
        ]
      },

      filtersModel: {
        lang           : 'Order.OrdersAll',
        filterOrderType: {
          key  : 'filter_type',
          value: 0,
          items: this.$t('Order.OrdersAll.Filter.filterOrderType.Items')
        },
        filterOrderReceipt: {
          key     : 'filter_order_receipt',
          value   : 0,
          items   : this.$t('Order.OrdersAll.Filter.filterOrderReceipt.Items'),
          disabled: true
        },
        filterWebhookSuccessful: {
          key     : 'filter_webhook_successful',
          value   : 0,
          items   : this.$t('Order.OrdersAll.Filter.filterWebhookSuccessful.Items'),
          disabled: true
        },
        filterOrderStatus: {
          key  : 'filter_status',
          value: [],
          items: this.$t('Order.OrdersAll.Filter.filterOrderStatus.Items')
        },
        filterIsScheduled: {
          key  : 'filter_is_scheduled',
          value: 0,
          items: this.$t('Order.OrdersAll.Filter.filterIsScheduled.Items')
        },
        filterSourceType: {
          key  : 'filter_source_type',
          value: null,
          items: []
        },
        filterPaymentType: {
          key  : 'filter_payment',
          value: null,
          items: []
        },
        filterPriceFrom: {
          key  : 'filter_price_from',
          value: null
        },
        filterPriceTo: {
          key  : 'filter_price_to',
          value: null
        },
        filterUser: {
          key  : 'filter_user',
          value: null,
          items: []
        },
        filterDate: {
          key    : 'filter_date',
          value  : null,
          visible: false,
          date   : null
        }
      },
      selectDateRangeDialogVisible: false,
      emailSelectDialogVisible    : false,
      emailSelectDialogExtraData  : null,
      selectedPrinter             : null
    }
  },
  computed: {
    EmailNotifyFromEnum() {
      return EmailNotifyFromEnum
    },
    FiscalDocsProviderTypeEnum() {
      return FiscalDocsProviderTypeEnum
    },
    SourceTypeEnum() {
      return SourceTypeEnum
    },
    OrderStatusEnum() {
      return OrderStatusEnum
    },
    FiscalDocTypeEnum() {
      return FiscalDocTypeEnum
    },
    PaymentTypeEnum() {
      return PaymentTypeEnum
    },
    filteredItems() {
      const items = this.dataTable?.data || []

      return items
        .map(o => {
          o.isSelectable = (parseInt(o.StatusId) === OrderStatusEnum.Completed || parseInt(o.StatusId) === OrderStatusEnum.Canceled) && this.userCanDeleteOrders
          return o
        })
    },
    customerColumnMaxWidth() {
      return this.$vuetify.breakpoint.lgAndDown ? 'max-width: 200px; width: 200px;' : 'max-width: 310px;  width: 310px;'
    },
    ordersToday() {
      const orders = this.$DataStore?.ordersDataToday?.items || []
      return orders.filter(order => parseInt(order.StatusId) > 0)
    },
    filterStaffList() {
      const users = clone(window?.appConfig?.POS_USERS || [])
      return users.map(o => {
        return {
          Id         : o.Id,
          Fullname   : o.Fullname,
          Description: o.Uid,
          Avatar     : {
            Icon : o.Active ? 'mdi-account-check-outline' : !o.Uid ? 'mdi-account-group' : 'mdi-account-alert-outline',
            Color: o.Active ? 'success' : !o.Uid ? 'info' : 'grey darken-1'
          }
        }
      })
    }
  },
  watch: {},
  created() {
    // console.log('1. created');
    this.$bus.$on(window.SocketCommand.Order.Webhook.History, this.getOrderWebhookHistoryResponse)
    this.$bus.$on(window.SocketCommand.Order.Webhook.Retry, this.orderWebhookRetryResponse)
    this.$bus.$on(window.SocketCommand.Order.DailyGet, this.updateDataFromDaily)
    this.$bus.$on(window.SocketCommand.Ajax.Get, this.onWebhookUpdateData)
    this.$bus.$on(SocketCommand.Order.Report.EmailOrdersReport, this.onEmailReportResponse)
    this.$bus.$on(SocketCommand.Order.Report.EmailReceiptsReport, this.onEmailReportResponse)

    // Init Custom Filter Values)
    this.filtersModel.filterSourceType.items = this.sourceTypesList
    this.filtersModel.filterPaymentType.items = this.paymentTypesFiltersList
    this.filtersModel.filterUser.items = this.filterStaffList
    this.filtersModel.filterOrderReceipt.disabled = !this.appHasFiscalDocsProvider

    if (this.appHasWebhooksOrderActive && (this.userIsOwner || this.userIsSuperAdmin)) {
      this.filtersModel.filterWebhookSuccessful.disabled = false
      this.dataTable.headers.splice(this.dataTable.headers.length - 1, 0, {
        text     : this.$t('Order.Headers.Transmission'),
        value    : 'webhook_successful',
        align    : 'center',
        sortable : true,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      }
      )
    }
  },
  mounted() {
    // console.log('2. mounted');
  },
  updated() {
    // console.log('3. updated');
  },
  beforeDestroy() {
    // console.log('4. destroyed');
    this.$bus.$off(window.SocketCommand.Order.Webhook.History, this.getOrderWebhookHistoryResponse)
    this.$bus.$off(window.SocketCommand.Order.Webhook.Retry, this.orderWebhookRetryResponse)
    this.$bus.$off(window.SocketCommand.Order.DailyGet, this.updateDataFromDaily)
    this.$bus.$off(window.SocketCommand.Ajax.Get, this.onWebhookUpdateData)
    this.$bus.$off(SocketCommand.Order.Report.EmailOrdersReport, this.onEmailReportResponse)
    this.$bus.$off(SocketCommand.Order.Report.EmailReceiptsReport, this.onEmailReportResponse)
  },
  methods: {
    getContrastColor,

    onEmailReportResponse(response, payload) {
      if (response?.status === 'success') {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Misc.Notification.EmailReport.Success'),
          type: 'info',
          icon: 'email'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Misc.Notification.EmailReport.Error'),
          type: 'error',
          icon: 'email'
        })
      }
    },

    updateDataFromDaily() {
      this.getData()
    },

    isOrderToday(order) {
      return !!this.ordersToday.find(o => parseInt(o.Id) === parseInt(order.Id))
    },

    showSelectOrderTypeTimeDialog(order) {
      this.$bus.$emit('order-type-time-dialog', order)
    },

    showAcceptOrderDialog(order) {
      this.$bus.$emit('show-order-accept-dialog', order)
    },

    showSelectEmailDialog(extraData) {
      this.emailSelectDialogExtraData = extraData || null
      this.emailSelectDialogVisible = true
    },
    onEmailSelected(emails, extraData) {
      if (!Array.isArray(emails) || !emails?.length) return

      if (extraData?.type === 'receiptTotalsZ') {
        this.SendEmailReceiptsReport(emails, extraData?.datePeriod || null, extraData?.staffId || null, extraData?.guid || null, extraData?.withoutOpenHours ?? false)
      } else {
        this.SendEmailOrdersReport(emails, extraData?.datePeriod || null)
      }
    },

    showSelectDateRangeDialog(printer, type = 'receiptTotalsZ', action = 'print') {
      this.selectedPrinter = {
        printer: printer,
        action : action,
        type   : type
      }
      this.selectDateRangeDialogVisible = true
    },
    onPrintReceiptTotalsZDateRangeSelected(data) {
      const {datePeriod, withoutOpenHours, staffId, guid} = data

      if (this.selectedPrinter.type === 'receiptTotalsZ') {
        if (this.selectedPrinter.action === 'print') {
          this.printReceiptTotalsZ(datePeriod || null, staffId || null, guid || null, withoutOpenHours ?? false, this.selectedPrinter.printer)
        } else if (this.selectedPrinter.action === 'email') {
          this.showSelectEmailDialog({
            type            : this.selectedPrinter.type,
            datePeriod      : datePeriod || null,
            staffId         : staffId || null,
            guid            : guid || null,
            withoutOpenHours: withoutOpenHours ?? false
          })
        } else if (this.selectedPrinter.action === 'pdf') {
          this.downloadPdfReceiptsReport(datePeriod || null, staffId || null, guid || null, withoutOpenHours ?? false)
        }
      } else {
        if (this.selectedPrinter.action === 'print') {
          this.printOrderTotalsZ(datePeriod || null, this.selectedPrinter.printer)
        } else if (this.selectedPrinter.action === 'email') {
          this.showSelectEmailDialog({
            type      : this.selectedPrinter.type,
            datePeriod: datePeriod || null
          })
        } else if (this.selectedPrinter.action === 'pdf') {
          this.downloadPdfOrdersReport(datePeriod || null)
        }
      }
      this.selectedPrinter = null
    }
  }
}
</script>

<style scoped>
:deep(.receipts-badge > .v-badge__wrapper > span) {
  height: 16px;
  min-width: 16px;
  font-size: 11px;
  font-weight: bold;
  line-height: 8px;
  padding: 5px 4px;
}
</style>
